import React, { useEffect, useState } from "react";
import Sidebar from "./Sidebar";
import { useDispatch, useSelector } from "react-redux";
import { getUsers, deleteUser } from "../../actions/userActions";
import { clearError, clearUserDeleted } from "../../slices/authSlice";
import Loader from "../Loader";
import { MdDelete, MdEdit, MdPerson, MdEmail, MdSecurity, MdCalendarMonth } from "react-icons/md";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";

const UserList = () => {
    const {
        users = [],
        loading = true,
        error,
        isUserDeleted,
    } = useSelector((state) => state.authState);
    const dispatch = useDispatch();
    const [searchQuery, setSearchQuery] = useState("");
    const [filteredUsers, setFilteredUsers] = useState([]);

    useEffect(() => {
        if (error) {
            toast(error, {
                position: toast.POSITION.BOTTOM_CENTER,
                type: "error",
                onOpen: () => {
                    dispatch(clearError());
                },
            });
            return;
        }
        if (isUserDeleted) {
            toast.success("User deleted successfully!", {
                position: toast.POSITION.BOTTOM_CENTER,
            });
            dispatch(clearUserDeleted());
        }
        dispatch(getUsers());
    }, [dispatch, error, isUserDeleted]);

    useEffect(() => {
        setFilteredUsers(
            users.filter((user) =>
                (user.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
                (user.email || "").toLowerCase().includes(searchQuery.toLowerCase())
            )
        );
    }, [users, searchQuery]);

    const handleDelete = (id) => {
        Swal.fire({
            title: "Delete User?",
            text: "This will permanently remove the user account from the system.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#D4AF37",
            cancelButtonColor: "#1A1A1A",
            confirmButtonText: "Confirm Delete",
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(deleteUser(id));
            }
        });
    };

    return (
        <div className="flex bg-gray-50 min-h-screen">
            <Sidebar />
            
            <div className="flex-1 p-8 lg:p-12 overflow-x-hidden">
                {loading ? (
                    <Loader />
                ) : (
                    <div className="max-w-7xl mx-auto">
                        <div className="flex flex-col md:flex-row md:items-center justify-between mb-12 gap-6">
                            <div>
                                <h1 className="text-3xl font-serif font-bold text-gray-900 mb-2">Member Directory</h1>
                                <p className="text-gray-500 text-sm font-light">Manage permissions and user profiles for your store.</p>
                            </div>

                            <div className="relative group min-w-[320px]">
                                <input
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    type="text"
                                    className="w-full bg-white border border-gray-100 px-6 py-4 rounded-2xl text-sm focus:border-yellow-600 outline-none transition-all shadow-sm group-hover:shadow-md"
                                    placeholder="Search by Name or Email..."
                                />
                                <div className="absolute right-6 top-1/2 -translate-y-1/2 text-gray-300">
                                    <MdPerson className="text-xl" />
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-[2rem] border border-gray-100 shadow-sm overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead>
                                        <tr className="border-b border-gray-50 bg-gray-50/30">
                                            <th className="px-8 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Identity</th>
                                            <th className="px-8 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Access Level</th>
                                            <th className="px-8 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest text-center">Registration</th>
                                            <th className="px-8 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest text-right">Settings</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50">
                                        {filteredUsers.map((user) => (
                                            <tr key={user._id} className="hover:bg-gray-50/50 transition-colors group">
                                                <td className="px-8 py-6">
                                                    <div className="flex items-center gap-4">
                                                        <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center text-yellow-600">
                                                            <MdPerson className="text-xl text-yellow-500" />
                                                        </div>
                                                        <div>
                                                            <div className="font-bold text-gray-900 font-serif text-sm">{user.name}</div>
                                                            <div className="text-xs text-gray-400 font-light flex items-center gap-1">
                                                                <MdEmail className="text-[10px]" /> {user.email}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-8 py-6">
                                                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${
                                                        user.role === 'admin' 
                                                        ? 'bg-purple-100 text-purple-700 border-purple-200' 
                                                        : 'bg-blue-100 text-blue-700 border-blue-200'
                                                    }`}>
                                                        <MdSecurity className="mr-1" /> {user.role}
                                                    </span>
                                                </td>
                                                <td className="px-8 py-6 text-center">
                                                    <div className="text-xs text-gray-600 font-medium flex items-center justify-center gap-1">
                                                        <MdCalendarMonth className="text-gray-300" /> 
                                                        {new Date(user.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                                                    </div>
                                                </td>
                                                <td className="px-8 py-6 text-right">
                                                    <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <Link 
                                                            to={`/admin/user/${user._id}`}
                                                            className="p-3 text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 rounded-xl transition-all"
                                                        >
                                                            <MdEdit className="text-xl" />
                                                        </Link>
                                                        <button 
                                                            onClick={() => handleDelete(user._id)}
                                                            className="p-3 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                                                        >
                                                            <MdDelete className="text-xl" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserList;
